<script lang="ts">
	import { type Achievement, type AchievementHeadlineInfo } from '../../../domain/Achievement';
	import AchievementProgress from '$components/home/achievement/elements/AchievementProgress.svelte';
	import AchievementBody from '$components/home/achievement/elements/AchievementBody.svelte';
	import {
		calculateExpirationStatus,
		formatPreviousDayForAriaLabel,
		formatPreviousDayWithDayAndMonth
	} from '$services/DateFormatter';
	import { ExpiryStatus } from '../../../domain/ExpiryStatus';
	import { instructionSheet } from '$components/home/SheetData';
	import AchievementFooter from '$components/home/achievement/elements/AchievementFooter.svelte';
	import AchievementBaseCard from '$components/home/achievement/AchievementBaseCard.svelte';


	export let achievement: Achievement;
	export let index: number;
	export let onclick: Function;
	export let isHorizontal: boolean = false;
	export let display: 'row' | 'grid';
	export let onViewPortCallback: Function | undefined = undefined;

	const getHeadline = (progressEndDate: string): AchievementHeadlineInfo => {
		const expirationStatus = calculateExpirationStatus(progressEndDate);

		if (expirationStatus === ExpiryStatus.TOMORROW) {
			return {
				text: 'Nur bis morgen'
			};
		} else if (expirationStatus === ExpiryStatus.TODAY) {
			return {
				text: 'Nur noch heute'
			};
		} else {
			const visibleDate = formatPreviousDayWithDayAndMonth(progressEndDate);
			const accesibleDate = formatPreviousDayForAriaLabel(progressEndDate);

			return {
				text: `Nur bis ${visibleDate}`,
				ariaLabel: `Nur bis ${accesibleDate}`
			};
		}
	};

	const headlineInfo: AchievementHeadlineInfo = getHeadline(achievement.content.progressEndDate);

</script>

<AchievementBaseCard achievement={achievement} display={display} index={index+1} {onViewPortCallback}>
	<oc-card-v2
		as-button=""
		class="card"
		data-oc-sheet-v1-create={JSON.stringify(instructionSheet(achievement.achievementId))}
		data-testid="achievement-activated-card"
		id="achievement-card-{index+1}"
		on:click={() => onclick()}
	>
		<div
			class="oc-p-100 card__content"
			class:card__content--horizontal={isHorizontal}
			data-testid="achievement-activated-card-content">
			<div class="card__body">
				<AchievementProgress completionGoal={achievement.content.card.completionGoal}
					currentProgress={achievement.progress.current}
					progressColor={achievement.content.card.progressBarColor} />
				<AchievementBody
					description={achievement.content.card.description}
					title={achievement.content.card.title}>
					<div aria-label={headlineInfo.ariaLabel} class="oc-copy-75 text-secondary">
						{headlineInfo.text}
					</div>
				</AchievementBody>

			</div>
			<AchievementFooter
				compensationAmount={achievement.content.card.compensationAmount}
				compensationHeadline={'Verdiene'}
				isHorizontal={isHorizontal}>
				<oc-button-v1
					data-testid="achievement-activated-card-button"
					oc-aria-label="Challenge aktiviert"
					icon-type-right="check"
					size="50"
					success="true"
					disabled
				>
					Aktiviert
				</oc-button-v1>
			</AchievementFooter>
		</div>
	</oc-card-v2>
</AchievementBaseCard>

<style lang="scss">
  @use '@otto-ec/otto-components-utils/scss/mixins' as mixins;
  @use "@otto-ec/design-tokens/tokens";

  .card {
    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;

      &--horizontal {
        @include mixins.breakpoint(mixins.$breakpoint-l) {
          flex-direction: row;
        }
      }
    }

    &__body {
      display: flex;
      align-items: center;
    }
  }

  .text-secondary {
    color: tokens.$oc-semantic-color-text-secondary
  }
</style>
